<template>
  <div class="dropdown-container p-relative gap-16 w-100">
    <label class="text-b14 color-black text-medium dropdown-label">
      {{ label }}
    </label>
    <div
      ref="dropdownContainer"
      class="dropdown-trigger d-flex items-center justify-between"
      @click="toggleDropdown"
      :class="{
        active: isOpen,
        error: hasError,
      }"
    >
      <span class="text-b14 color-on-cont-bright leaning-24" :class="{ 'color-on-cont-dim': !selectedOption }">{{ selectedOption ? selectedOption.label : placeholder }}</span>
      <Icon name="arrow-blue" class="arrow-down" />
    </div>

    <Transition name="slide-fade">
      <div v-if="isOpen" class="dropdown-options p-absolute bg-cont-white">
        <div
          v-for="option in options"
          :key="option.value"
          class="dropdown-option d-flex items-center leaning-24"
          :class="{ selected: option.value === modelValue }"
          @click="selectOption(option)"
        >
          <span class="text-b14">{{ option.label }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, toRefs, watch, inject, getCurrentInstance, onBeforeUnmount } from 'vue';
import Icon from '@/components/base/icon/index.vue';
import type { InjectFormType, ValidFunction } from '@/types';

interface DropdownOption {
  label: string;
  value: string | number;
}

interface ValidationResult {
  valid: boolean;
  error: string | null;
}

interface Props {
  modelValue: string | number | null;
  options: DropdownOption[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  rules?: 'required' | 'optional';
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'انتخاب کنید',
  label: '',
  disabled: false,
});

const emit = defineEmits(['update:modelValue']);
const { options, modelValue, disabled, rules, label } = toRefs(props);
const isOpen = ref<boolean>(false);
const hasError = ref<boolean>(false);
const selectedOption = computed(() => {
  return options.value.find((opt) => opt.value === modelValue.value);
});

const isRequired = computed(() => {
  return rules.value?.includes('required') ?? false;
});

const toggleDropdown = () => {
  if (disabled.value) return;
  isOpen.value = !isOpen.value;
};

const selectOption = (option: DropdownOption) => {
  emit('update:modelValue', option.value);
  isOpen.value = false;
};

const validate = (): ValidationResult => {
  if (isRequired.value && !modelValue.value) {
    hasError.value = true;
    return { valid: false, error: `لطفا ${label.value || 'گزینه'} را انتخاب کنید` };
  }
  hasError.value = false;
  return { valid: true, error: null };
};
watch(modelValue, () => {
  if (hasError.value) {
    hasError.value = false;
  }
});

const form = inject<InjectFormType>('form');
const uid = getCurrentInstance()?.uid as number;
const dropdownContainer = ref<HTMLElement | null>(null);

const handleClickOutside = (e: MouseEvent) => {
  if (isOpen.value && dropdownContainer.value && !dropdownContainer.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  if (form) {
    form.bind({ validate: validate as unknown as () => ValidFunction, uid });
  }

  setTimeout(() => {
    document.addEventListener('mousedown', handleClickOutside);
  }, 0);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<style lang="scss" scoped>
@import url('./index.scss');
</style>
